import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getPublicTrackingData, getTrackingData } from '../api';
import { useBreadcrumbs } from '../../../app/breadcrumb-provider';
import { TrackingData } from '../../../types/tracking';
import TrackingPageFTL from './TrackingPageFTL';
import TrackingPagePTL from './TrackingPagePTL';

interface TrackingPageProps {
    isPublic?: boolean;
}

const TrackingPage: React.FC<TrackingPageProps> = ({ isPublic = false }) => {
    const [searchParams] = useSearchParams();
    const { cnNumber } = useParams<{ cnNumber: string }>();
    const navigate = useNavigate();
    const { setBreadcrumbs } = useBreadcrumbs();
    const [trackingData, setTrackingData] = useState<TrackingData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [trackingSuccess, setTrackingSuccess] = useState(true);

    const refreshTrackingData = useCallback(async () => {
        try {
            let response = null;
            if (isPublic) {
                const token = searchParams.get("token");
                if (!token) return;

                response = await getPublicTrackingData(token);
            } else {
                if (!cnNumber) return;

                response = await getTrackingData(cnNumber);
            }

            if (response?.success) {
                setTrackingData(response);
                setTrackingSuccess(true);
            } else {
                setTrackingSuccess(false);
            }
        } catch (err) {
            console.error("Error refreshing tracking data:", err);
        }
    }, [cnNumber, isPublic, searchParams]);

    // Set breadcrumbs
    useEffect(() => {
        setBreadcrumbs([
            { label: 'Shipments', href: '/shipments' },
            { label: `Track ${cnNumber || ''}`, href: '#' }
        ]);
    }, [setBreadcrumbs, cnNumber]);

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                let trackingResponse = null;

                if (isPublic) {
                    const token = searchParams.get("token");

                    if (!token) {
                        setError("Tracking token is missing");
                        setTrackingSuccess(false);
                        return;
                    }
                    trackingResponse = await getPublicTrackingData(token);
                } else {
                    if (!cnNumber) {
                        setError("CN Number is required");
                        setTrackingSuccess(false);
                        return;
                    }
                    trackingResponse = await getTrackingData(cnNumber);
                }

                if (!isMounted) return;

                if (trackingResponse?.success) {
                    setTrackingData(trackingResponse);
                    setTrackingSuccess(true);
                } else {
                    setTrackingSuccess(false);
                }

            } catch (err) {
                if (!isMounted) return;
                console.error("Error in fetchData:", err);
                setError("Failed to load tracking data");
                setTrackingSuccess(false);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [cnNumber, isPublic, searchParams]);

    if (loading) {
        return (
            <div className="h-full w-full flex items-center justify-center bg-gray-50/30 absolute inset-0">
                <div className="flex justify-center space-x-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="text-center">
                    <div className="text-lg font-medium text-red-600 mb-2">Error loading tracking data</div>
                    <div className="text-sm text-gray-600">{error}</div>
                    <button
                        onClick={() => navigate(`/shipments`)}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Back to Shipments
                    </button>
                </div>
            </div>
        );
    }

    if (!trackingSuccess || !trackingData) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="text-center">
                    <div className="text-lg font-medium text-gray-900 mb-2">Tracking Not Available</div>
                    <div className="text-sm text-gray-600">Tracking information is not available for this shipment</div>
                    <button
                        onClick={() => navigate(`/shipments`)}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Back to Shipments
                    </button>
                </div>
            </div>
        );
    }

    if (trackingData.tracking_mode === 'ptl') {
        return <TrackingPagePTL data={trackingData} isPublic={isPublic} />;
    }

    if (trackingData.tracking_mode === 'ftl') {
        return <TrackingPageFTL data={trackingData} refreshData={refreshTrackingData} isPublic={isPublic} />;
    }
    return (
        <div className="h-full flex items-center justify-center">
            <div className="text-center">
                <div className="text-lg font-medium text-red-600 mb-2">Unknown Tracking Mode</div>
                <div className="text-sm text-gray-600">This shipment has an unsupported tracking mode: {(trackingData as any).tracking_mode}</div>
            </div>
        </div>
    );
};

export default TrackingPage;
